import {
	alpha,
	Box,
	ButtonBase,
	keyframes,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Plus, Sparkles, Target } from "lucide-react";
import type { FC } from "react";
import { CTA_GRADIENT, CTA_SHADOW } from "web_myplann/Goals/Types/constants";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const pulseRing = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.5; transform: scale(1.08); }
`;

type Props = {
	title: string;
	onClick?: () => void;
};

export const AddGoalCta: FC<Props> = ({ title, onClick }) => {
	const { t } = useResource();
	const { palette } = useTheme();

	return (
		<Box px={1.6} py={1.2}>
			<Box
				sx={{
					borderRadius: "12px",
					overflow: "hidden",
					position: "relative",
					background: CTA_GRADIENT,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						overflow: "hidden",
						pointerEvents: "none",
					}}
				>
					<Target
						size={32}
						style={{
							position: "absolute",
							top: 12,
							left: 12,
							color: alpha(palette.common.white, 0.07),
						}}
					/>
					<Sparkles
						size={40}
						style={{
							position: "absolute",
							bottom: 16,
							right: 12,
							color: alpha(palette.common.white, 0.07),
						}}
					/>
				</Box>

				<Stack
					alignItems="center"
					textAlign="center"
					px={2.5}
					py={3.5}
					position="relative"
				>
					<Box sx={{ position: "relative", mb: 2 }}>
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								borderRadius: "50%",
								border: `2px solid ${alpha(palette.common.white, 0.3)}`,
								animation: `${pulseRing} 2s ease-in-out infinite`,
							}}
						/>
						<Box
							sx={{
								width: 56,
								height: 56,
								borderRadius: "50%",
								bgcolor: alpha(palette.common.white, 0.2),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Target size={28} color={palette.common.white} />
						</Box>
					</Box>

					<Typography
						fontSize={16}
						fontWeight={700}
						color={palette.common.white}
						mb={0.4}
					>
						{title}
					</Typography>
					<Typography
						fontSize={12}
						color={alpha(palette.common.white, 0.8)}
						mb={2.5}
					>
						{t(Resources.Goals.List.ctaDescription)}
					</Typography>

					<ButtonBase
						onClick={onClick}
						sx={{
							bgcolor: palette.common.white,
							color: palette.blue.main,
							fontWeight: 600,
							fontSize: 14,
							px: 3,
							py: 1.2,
							borderRadius: "16px",
							display: "flex",
							alignItems: "center",
							gap: 0.6,
							boxShadow: CTA_SHADOW,
							"&:active": {
								transform: "scale(0.97)",
							},
						}}
					>
						<Plus size={16} />
						{t(Resources.Goals.List.ctaButton)}
					</ButtonBase>
				</Stack>
			</Box>
		</Box>
	);
};
